import { ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validator, ValidatorFn, Validators } from '@angular/forms';
import { Attribute } from '../../../../models/masters/attribute.interface';
import { selectValidator } from '../../../../validators/select-validator';
import { LoadingService } from '../../../../services/loading.service';
import { forkJoin, Observable, tap } from 'rxjs';
import { MasterService } from '../../../../services/masters-service';
import { SharedModule } from '../../../../modules/shared/shared.module';
import { MultiSelectComponent } from '../../../select/multi-select.component';
import { AutocompleteDropdownComponent } from '../../../autocomplete-dropdown/autocomplete-dropdown.component';
import { BooleanType, InventoryType } from '../../../../constants/app-constants';
import {JobOrderServiceForm, JobOrderVendorService } from '../../../../models/job-orders.interface';
import { ServiceMaster } from '../../../../models/service-master.interface';
import { JobOrderService } from '../../../../services/job.order.service';
import { ServiceType } from '../../../../constants/master-constants';
import { FilterCondition } from '../../../../models/table-list-request.interface';

@Component({
  selector: 'app-vendor-service-form',
  standalone: true,
  imports: [SharedModule, MultiSelectComponent, AutocompleteDropdownComponent],
  templateUrl: './vendor-service-form.component.html',
  styleUrl: './vendor-service-form.component.scss'
})
export class VendorServiceFormComponent {

  @Input() jobOrderVendorService!: JobOrderVendorService;
  @Input() defaultAttributes: Attribute[] = [];
  @Input() inventoryOptions: any[] = [];
  @Input() inventoryType: InventoryType = InventoryType.CONTAINER;
  @Input() serviceType: ServiceType = ServiceType.GENERAL_SERVICE;
  @Input() loadedOnModal: boolean = false;
  @Input() allowInventorySelection: boolean = true;

  @Output() onSave = new EventEmitter<{jobOrderVendorService: JobOrderVendorService, appliedInventoryIds: string[]}>();
  
  serviceForm!: FormGroup;
  attributes: Attribute[] = [];
  isEdit: boolean = false;
  booleanOptions = Object.entries(BooleanType).map(([key, value]) => ({ key: value.key, value: value.value }));
  

  serviceSearchQuery: string = '';
  selectedService!: ServiceMaster | null;

  serviceVendorList: any[] = [];
  serviceEquipmentList: any[] = [];

  additionalFilters: FilterCondition[] = [];

  constructor(private fb: FormBuilder,
              private loadingService: LoadingService,
              private masterService: MasterService,
              private jobOrderService: JobOrderService,
              private cdr: ChangeDetectorRef) {
    this.initForm();
  }

  private addServiceTypeFilter(): void {
    if (this.serviceType) {
      this.additionalFilters.push({
        name: 'SERVICE_TYPE_EQUALS',
        value: [this.serviceType.key],
        alias: 'serviceType'
      });
    }
    this.additionalFilters.push({
      name: 'ENTITY_TYPE_EQUALS',
      value: [this.inventoryType.key],
      alias: 'entityType'
    });
  }
  
  private initForm(): void {
    this.serviceForm = this.fb.group({
      serviceId: [null, Validators.required],
      vendorId: [null, selectValidator()],
      equipmentIds: [null],
      inventoryIds: [null]
    });
  }

  ngOnInit() {
    this.isEdit = !!this.jobOrderVendorService;
    this.allowInventorySelection =  this.allowInventorySelection && !this.isEdit;
    this.hydrateForm();
    this.addServiceTypeFilter();
  }

  private addAttributesToForm(): void {    
    this.attributes.forEach(attr => {
      const validators = this.getValidators(attr);
      
      let defaultValue: any = '';
      
      if (attr.defaultValues && attr.defaultValues.length > 0) {
        defaultValue = attr.defaultValues[0].value;
      }
      
      if (attr.dataType === 'BOOLEAN' && defaultValue !== '') {
        defaultValue = defaultValue === 'true';
      }
      
      this.serviceForm.addControl(
        attr.symbol,
        this.fb.control(defaultValue, validators)
      );
    });
  }

  getValidators(attribute: Attribute): ValidatorFn[] {
      if (!attribute.mandatory) return [];
      if (attribute.dataType === 'BOOLEAN') return [selectValidator()];
      return [Validators.required];
  }

  onServiceSelection(result: any) {
    this.initialiseService(result.id).subscribe({
      next: () => {},
      error: (err) => {
        console.error('Failed to initialize service:', err);
      }
    });
  }

  hydrateForm() {
    if (!this.isEdit) return;
    this.initialiseService(this.jobOrderVendorService.serviceId).subscribe({
      next: () => {
        this.cdr.detectChanges(); 
        this.patchFormWithExistingValues(this.jobOrderVendorService);
      },
      error: (err) => {
        console.error('Failed to initialize service:', err);
      }
    });
  }

  initialiseService(serviceId: string): Observable<any> {
    this.loadingService.show();
    return forkJoin({
      serviceResult: this.masterService.getServiceById(serviceId),
      vendorResult: this.masterService.listVendorsByService(serviceId, true),
      equipmentResult: this.masterService.listEquipmentsByService(serviceId, true)
    }).pipe(
      tap({
        next: ({serviceResult, vendorResult, equipmentResult}: any) => {
          this.selectedService = serviceResult.data;
          this.serviceVendorList = this.prepareVendorOptions(vendorResult.data);
          this.serviceEquipmentList = equipmentResult.data;
          this.attributes = this.selectedService?.serviceAttributes ?? [];
          this.addAttributesToForm();
          this.setDefaultVendorAndEquipments();
          console.log("completed intialisation");
        },
        error: (err) => {
          console.error(err);
        },
        finalize: () => this.loadingService.hide()
      })
    );
  }

  serviceSearchCleared() {
    this.selectedService = null;
    this.serviceForm.get("vendorId")?.setValue(null);
    this.serviceForm.get("equipmentIds")?.setValue(null);
  }

  saveForm() {
    this.serviceForm.markAllAsTouched();
    if (!this.serviceForm.valid) {
      this.jobOrderService.printFormErrors(this.serviceForm);
      return;
    }
    const serviceData = this.createOrUpdateServiceFromForm(this.jobOrderVendorService);
    if (this.loadedOnModal) {
      this.closeModal(serviceData);
    }
    this.onSave.emit(serviceData);
  }

  closeModal!: (params?: any) => void;

  onClose(params?: any) {
    if (this.closeModal) {
      this.closeModal(params);
    }
  }

  setDefaultVendorAndEquipments() {
    if (this.isEdit) return;

    if (this.serviceVendorList.length > 0) {
      const selectedVendors = (() => {
        const vendor = this.serviceVendorList.find((vendor: any) => vendor.default);
        return vendor ? [vendor] : [];
      })();
      this.serviceForm.get("vendorId")?.setValue(selectedVendors);
    }
    if (this.serviceEquipmentList.length > 0) {
      const selectedEquipments = this.serviceEquipmentList.filter((equipment: any) => equipment.default);
      this.serviceForm.get("equipmentIds")?.setValue(selectedEquipments);
    }
  }

  prepareVendorOptions(vendorList: any[]): any[] {
    return vendorList.map(vendor => ({
      vendorId: vendor.vendorId,
      vendorName: vendor.vendorMetadata.name,
      default: vendor.default
    }));
  }

  patchFormWithExistingValues(existingData: JobOrderVendorService): void {
    if (!this.isEdit) return;

    this.serviceForm.patchValue({
      serviceId: {key: existingData.serviceId, value: existingData.metadata?.serviceName},
      vendorId: existingData.vendorId || '',
      equipmentIds: existingData.equipmentIds || []
    });
  
    if (existingData.attributes && this.attributes) {
      const attributeValues: Record<string, any> = {};
      existingData.attributes.forEach(existingAttr => {
        const matchingAttr = this.attributes.find(attr => attr.id === existingAttr.attributeId);
      
        if (matchingAttr && existingAttr.values?.length) {
          const firstValue = existingAttr.values[0].value;        
          attributeValues[matchingAttr.symbol] = this.convertValueForForm(firstValue, matchingAttr.dataType);
        }
      });
  
      this.serviceForm.patchValue(attributeValues);
    }
  }

  private convertValueForForm(value: any, dataType?: string): any {
    switch (dataType) {
      case 'BOOLEAN':
        return Boolean(value);
      case 'DECIMAL':
        return Number(value);
      default:
        return String(value);
    }
  }

  createOrUpdateServiceFromForm(existingService?: JobOrderVendorService): {jobOrderVendorService: JobOrderVendorService, appliedInventoryIds: string[]} {
    const formValue = this.serviceForm.value;
  
    const attributes = this.attributes
    .filter(attr => {
      const hasRequiredFields = attr.id && attr.symbol;
      const hasFormValue = formValue[attr.symbol] !== undefined && 
                          formValue[attr.symbol] !== null &&
                          formValue[attr.symbol] !== '';
      return hasRequiredFields && hasFormValue;
    })
    .map(attr => ({
      attributeId: attr.id as string,
      symbol: attr.symbol as string,
      name: attr.name as string,
      values: [{
        value: formValue[attr.symbol],
        text: String(formValue[attr.symbol])
      }],
    }));

    const serviceData: any = {
      serviceId: formValue.serviceId?.key || formValue.serviceId || undefined,
      vendorId: formValue.vendorId.vendorId || undefined,
      equipmentIds: formValue.equipmentIds?.map((eq: any) => eq.equipmentId) || undefined,
      attributes: attributes.length > 0 ? attributes : undefined,
      metadata: {
        vendorName: formValue.vendorId?.vendorName || '',
        serviceName: this.selectedService?.name || '',
        equipmentDetails: formValue.equipmentIds?.map((equipment: any) => ({
          id: equipment.equipmentId,
          name: equipment.equipmentName
        }))
      }
    };

    if (existingService) {
      Object.keys(serviceData).forEach(key => {
        if (key in existingService) {
          (existingService as any)[key] = (serviceData as any)[key];
        }
      });
      return { jobOrderVendorService: existingService, appliedInventoryIds: [] };
    }

    return { jobOrderVendorService: serviceData, appliedInventoryIds: formValue.inventoryIds || [] };
  }

}
